import { computed, readonly, ref, watch } from "vue";
import { LIST_ORGANIZATION_DESCENDANTS } from "../../graphql/organizations";
import useAccessibleScope from "../authentication/useAccessibleScope";
import { facilityLocationCode } from "../common/helpers/facility";
import {
  buildOrganizationHierarchy,
  linkScopeAncestors,
  collapseToBranchPoint,
} from "./helper";

const regions = ref([]);
const facilities = ref([]);
const allFacilities = ref([]); // Backup for all facilities
const otherFacilities = ref([]); // Facilities not under any region
const selectedRegions = ref([]);
const selectedFacilities = ref([]);
const organizationsLoading = ref(false);
const organizationTree = ref([]);
// The same hierarchy BEFORE `collapseToBranchPoint` trims single-child levels.
// `organizationTree` is the display tree; selection must be resolved against the
// untrimmed one, or facilities parented to a trimmed-away ancestor match no node
// and drop out of the facility list entirely.
const organizationHierarchy = ref([]);
const selectedFacility = ref({});
// When the org set comes from the user's accessible-scope union (rather than a
// single root's descendants), facilities whose parent region is out of scope
// (disjoint scopes granted directly on a facility) must still be listed.
const includeOrphanFacilities = ref(false);

// Identity of the region set the current selection was defaulted from. Used to
// re-default the selection to the full scope whenever the loaded region set
// changes (see ingestOrganizations) instead of latching the very first emission.
let lastRegionIdsKey = null;

// Handle on the live organizations subscription. These refs are module-level
// singletons, so a watch left running by another page keeps emitting into them
// and can narrow the dashboard's scope after the fact.
let organizationsSubscription = null;

// Teardown for the accessible-scope watcher, which replaces the second query.
let stopScopeWatch = null;

export default function useDashboard() {
  // Shared handling for both fetch paths (single-root descendants and the
  // accessible-scope union), which return the org list under different keys.
  function ingestOrganizations(
    organizations,
    queryLoading,
    { includeOrphans }
  ) {
    organizationsLoading.value = queryLoading;
    if (!organizations) {
      // Never leave the selector skeletal on a falsy/error emission.
      if (!queryLoading) organizationsLoading.value = false;
      return;
    }

    // Carried per emission rather than set by whichever fetch ran last, so a
    // stale emission cannot change the meaning of the current data.
    includeOrphanFacilities.value = includeOrphans;

    const nonDemoOrganizations = noDemoOrganizationsFilter(organizations);
    // Reconstruct ancestor links so a facility's real region/organization path is
    // known even when its parentId was nulled by the cache and when its ancestor
    // orgs/regions were not themselves returned by the union query (see
    // linkScopeAncestors). The single-root descendants query carries no ancestors,
    // so those nodes pass through with their own parentId intact.
    const linkedOrganizations = linkScopeAncestors(nonDemoOrganizations);
    populateRegions(linkedOrganizations);
    populateFacilities(linkedOrganizations);
    selectedFacility.value = facilities.value[0];

    // Default to the FULL loaded scope whenever the region set changes — first
    // load, a new login, or (critically) a later watchQuery emission that widens
    // a partial/cached first result. The previous one-shot `initialized` latch
    // only selected "all" on the first emission, so a narrow first result (e.g. a
    // stale single-org cache hit) stayed latched even after the full set arrived
    // — the dashboard then showed only one organization while the header (a
    // separate source) showed all.
    const regionIdsKey = regions.value
      .map((region) => region.id)
      .sort()
      .join(",");
    if (regionIdsKey !== lastRegionIdsKey) {
      lastRegionIdsKey = regionIdsKey;
      selectedRegions.value = [...regions.value];
    }
    // Recompute the displayed/selected facilities against the freshly loaded
    // data. The selectedRegions watch only fires when the selection changes, so a
    // re-fetch that keeps the same selection would otherwise leave them stale.
    updateSelectedFacilities();

    organizationsLoading.value = false;
  }

  // Subscribes directly instead of through `apolloWatchQuery`, which discards the
  // subscription and so offers no way to stop emitting. Starting a new watch
  // always tears down the previous one, so only one source can write to the
  // shared refs at a time.
  function watchOrganizations(apolloClient, queryProps, onResult) {
    stopOrganizationsWatch();
    const observableQuery = apolloClient.watchQuery(queryProps);
    organizationsSubscription = observableQuery.subscribe({
      next({ data, loading }) {
        onResult(data, loading);
      },
      error() {
        // Errors are logged by the apollo link; clear the skeleton so a failed
        // load does not leave the selector loading forever.
        organizationsLoading.value = false;
      },
    });
    return observableQuery;
  }

  function stopOrganizationsWatch() {
    if (stopScopeWatch) {
      stopScopeWatch();
      stopScopeWatch = null;
    }

    if (!organizationsSubscription) return;
    organizationsSubscription.unsubscribe();
    organizationsSubscription = null;
  }

  function fetchOrganizations(apolloClient, id, types) {
    organizationsLoading.value = true;
    return watchOrganizations(
      apolloClient,
      {
        query: LIST_ORGANIZATION_DESCENDANTS,
        variables: {
          id,
          types,
        },
        fetchPolicy: "cache-first",
      },
      ({ organizationDescendants }, queryLoading) =>
        // Include orphan facilities so a facility whose parentId doesn't resolve
        // to a returned region node is still listed rather than dropped. In a
        // well-formed single-root tree every facility sits under a returned
        // region, so `otherFacilities` is empty and this changes nothing.
        ingestOrganizations(organizationDescendants, queryLoading, {
          includeOrphans: true,
        })
    );
  }

  // Source the region/facility selectors from the user's navigable-scope union
  // (all subtrees where they hold a role) instead of one root's descendants, so
  // multi-org managers see every scope — including disjoint standalone facilities.
  // Reads the scope `useAccessibleScope` already derived from the login
  // response rather than issuing a second `myOrganizations` query. Two sources
  // for the same set could disagree, with the header switcher and the dashboard
  // selectors then showing different organizations.
  function fetchAccessibleOrganizations(_apolloClient, types) {
    stopOrganizationsWatch();
    organizationsLoading.value = true;

    const { accessibleOrganizations } = useAccessibleScope();
    const wanted = Array.isArray(types) && types.length ? new Set(types) : null;

    stopScopeWatch = watch(
      accessibleOrganizations,
      (organizations) => {
        const scoped = wanted
          ? (organizations || []).filter((o) => wanted.has(o.type))
          : organizations || [];

        ingestOrganizations(scoped, false, { includeOrphans: true });
      },
      { immediate: true }
    );

    return stopScopeWatch;
  }

  function noDemoOrganizationsFilter(organizations) {
    return organizations.filter((organization) => !organization.isDemo);
  }

  function populateRegions(organizationDescendants) {
    const regionsList = organizationDescendants.filter((descendant) =>
      ["region", "organization"].includes(descendant.type)
    );

    // Root the tree at the lowest common ancestor: build the full org/region
    // hierarchy, then drop single-child levels above the branch point so the tree
    // starts where the scope actually branches (matches the side-menu tree).
    const hierarchy = buildOrganizationHierarchy(
      regionsList.map((node) => ({ ...node }))
    );
    organizationHierarchy.value = hierarchy;
    organizationTree.value = hierarchy.map(collapseToBranchPoint);

    regions.value = regionsList;
  }

  function populateFacilities(organizationDescendants) {
    // Hoist the location code out of the ProfileUnion so the facility selector can
    // filter and label on one flat field, and so the union shape never reaches the
    // autocomplete's return-object model.
    allFacilities.value = organizationDescendants
      .filter((descendant) => descendant.type === "facility")
      .map((facility) => ({
        ...facility,
        locationCode: facilityLocationCode(facility),
      }));

    // Separate facilities into those belonging to regions / organizations and those without a region /organization
    const regionIds = new Set(regions.value.map((region) => region.id));
    otherFacilities.value = allFacilities.value.filter(
      (facility) => !regionIds.has(facility.parentId)
    );
  }

  // Expand the selected leaf nodes (v-treeview selection-type="leaf" only keeps
  // leaves) into the full set of "fully selected" node ids: every leaf that is
  // selected, plus any ancestor whose entire leaf-subtree is selected. This lets
  // facilities attached directly to a non-leaf node (e.g. an org, or a parent
  // region with child regions) show when that node is wholly selected — they'd
  // otherwise be missed because the ancestor is never in the leaf model.
  function fullySelectedIds(nodes, selectedLeafIds, result = new Set()) {
    let allSelected = true;
    (nodes || []).forEach((node) => {
      let nodeSelected;
      if (node.children && node.children.length) {
        nodeSelected = fullySelectedIds(node.children, selectedLeafIds, result);
      } else {
        nodeSelected = selectedLeafIds.has(node.id);
      }
      if (nodeSelected) result.add(node.id);
      else allSelected = false;
    });
    return allSelected;
  }

  // Function to update selected facilities based on selected regions / organizations
  function updateSelectedFacilities() {
    const selectedLeafIds = new Set(
      selectedRegions.value.map((region) => region.id)
    );
    const selectedRegionIds = new Set();
    // Walk the untrimmed hierarchy: `organizationTree` omits the ancestor levels
    // `collapseToBranchPoint` dropped, and a facility parented to one of those
    // would match neither this set nor `otherFacilities` and vanish. Leaf nodes
    // are identical in both trees, so the treeview's leaf model still applies.
    fullySelectedIds(
      organizationHierarchy.value,
      selectedLeafIds,
      selectedRegionIds
    );

    const filteredFacilities = allFacilities.value.filter((facility) =>
      selectedRegionIds.has(facility.parentId)
    );

    // In accessible-scope (union) mode, always include facilities that sit
    // outside any in-scope region — scopes granted directly on a facility in a
    // different tree — so a multi-org manager sees them alongside the region's.
    facilities.value = includeOrphanFacilities.value
      ? [...filteredFacilities, ...otherFacilities.value]
      : filteredFacilities;

    selectedFacilities.value = facilities.value;
  }

  watch(selectedRegions, updateSelectedFacilities);

  // The scope every dashboard query should send. Single source so the
  // "non-empty list or skip" rule cannot drift between call sites: a query that
  // scopes by facility must never fall back to a single organization, and must
  // not run at all while this is empty.
  const facilityIds = computed(() =>
    selectedFacilities.value.map((facility) => facility.id)
  );

  // Function to reset global states
  function clearDashboardData() {
    stopOrganizationsWatch();
    regions.value = [];
    facilities.value = [];
    allFacilities.value = [];
    otherFacilities.value = [];
    selectedRegions.value = [];
    selectedFacilities.value = [];
    organizationsLoading.value = false;
    lastRegionIdsKey = null;
    includeOrphanFacilities.value = false;
    organizationTree.value = [];
    organizationHierarchy.value = [];
  }

  return {
    fetchOrganizations,
    fetchAccessibleOrganizations,
    regions: readonly(regions),
    facilities: readonly(facilities),
    selectedRegions,
    selectedFacilities,
    organizationsLoading,
    organizationTree: readonly(organizationTree),
    clearDashboardData,
    selectedFacility,
    facilityIds,
    stopOrganizationsWatch,
  };
}
